/**
 * How a fact reads on screen. An alert's facts arrive as exact decimal strings
 * in their unit (the facts contract, `insight_writer/facts.py`); `formatFactValue`
 * writes them out exactly as the API's own renderer (`insight_writer/formatting.py`)
 * writes the same fact into an alert's explanation — the same half-up rounding,
 * done on the decimal string itself rather than through a binary float, the same
 * currency symbols and the same words — so the figures panel never disagrees with
 * the sentence above it. It formats; it never computes a new figure. A digest's
 * facts arrive already rendered and are shown as given.
 *
 * This is a port, so it can drift: `__tests__/factFormat.test.ts` pins it to
 * golden outputs taken from `formatting.py`. An instant is written in the
 * tenant's reporting time zone, from its branding settings. Tech debt: the alerts API returning
 * each fact's rendered text, as the digest does, would remove the port.
 */

import type { FactConfidence, FactUnit } from '@/api/types'
import { minorUnitExponent } from '@/design/format'

/** What each fact kind measures, in the product's words. An unknown kind reads as its own name. */
const FACT_KIND_LABELS: Record<string, string> = {
  order_value_total: 'Order value',
  orders_total: 'Orders',
  new_customers: 'New customers',
  conversion_rate: 'Conversion rate',
  competition_sell_through: 'Sell-through',
  competition_velocity: 'Sales pace',
  competition_tickets_per_hour: 'Tickets an hour',
  competition_order_value_per_hour: 'Order value an hour',
  competition_pacing: 'Pacing',
  competition_projected_sellout: 'Projected sell-out',
  competition_projected_miss: 'Projected shortfall',
  competition_sales_slowdown: 'Recent sales pace',
  competition_ending_soon: 'Ending soon',
  ad_spend: 'Ad spend',
  ad_spend_no_purchases: 'Ad spend with no purchases',
  roas: 'Return on ad spend',
  funnel_step_rate: 'Step conversion rate',
  unattributed_share: 'Unattributed share',
  connector_unhealthy: 'Time since the last good connector run',
  meta_capture_rate: 'Meta capture rate',
  capture_corrected_roas: 'Return on ad spend, corrected for tracking',
  new_customer_value_30d: 'Expected 30-day value of new customers',
  ad_influenced_offplatform_share: 'Ad-influenced off-platform share',
}

export function factKindLabel(kind: string): string {
  const label = FACT_KIND_LABELS[kind]
  if (label) return label
  const words = kind.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** What a comparison is against (the facts contract's `ComparisonBasis`). */
const COMPARISON_BASIS_LABELS: Record<string, string> = {
  previous_period: 'the period before',
  own_7d_baseline: 'its own seven-day pace',
  straight_line_target: 'a straight line from start to end',
  capacity: 'capacity',
}

export function comparisonBasisLabel(basis: string): string {
  return COMPARISON_BASIS_LABELS[basis] ?? basis.replace(/_/g, ' ')
}

export const CONFIDENCE_LABELS: Record<FactConfidence, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
}

// --- exact decimals ---------------------------------------------------------------

/** A decimal string as sign, integer digits and fraction digits. */
interface Decimal {
  negative: boolean
  whole: string
  fraction: string
}

const DECIMAL = /^([+-])?(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/

/** `"-12.50"` → `{negative, "12", "50"}`, an exponent applied; null for anything that is not a finite decimal. */
function parseDecimal(value: string): Decimal | null {
  const match = DECIMAL.exec(value.trim())
  if (!match) return null
  const digits = (match[2] ?? '') + (match[3] ?? '')
  const point = (match[2] ?? '').length + Number(match[4] ?? 0)
  const padded = point < 0 ? '0'.repeat(-point) + digits : point > digits.length ? digits + '0'.repeat(point - digits.length) : digits
  const at = Math.max(point, 0)
  return { negative: match[1] === '-', whole: padded.slice(0, at).replace(/^0+(?=\d)/, '') || '0', fraction: padded.slice(at) }
}

/** The decimal times 10^`places` (a shift of the point, not a multiplication). */
function shift(value: Decimal, places: number): Decimal {
  const digits = value.whole + value.fraction
  return parseDecimal(`${value.negative ? '-' : ''}${digits.length ? digits : '0'}e${places - value.fraction.length}`) as Decimal
}

/** Rounded to `places` decimals, half away from zero (Python's `ROUND_HALF_UP`), on the digits themselves. */
function quantize(value: Decimal, places: number): Decimal {
  const fraction = value.fraction.padEnd(places + 1, '0')
  const kept = value.whole + fraction.slice(0, places)
  const roundUp = Number(fraction[places]) >= 5
  let digits = kept
  if (roundUp) {
    const carried = [...kept].reverse()
    let index = 0
    for (; index < carried.length; index += 1) {
      if (carried[index] === '9') {
        carried[index] = '0'
      } else {
        carried[index] = String(Number(carried[index]) + 1)
        break
      }
    }
    digits = (index === carried.length ? '1' : '') + carried.reverse().join('')
  }
  const whole = digits.slice(0, digits.length - places).replace(/^0+(?=\d)/, '') || '0'
  // The sign is kept even when the figure rounds to zero, as `Decimal` keeps it ("-0.00").
  return { negative: value.negative, whole, fraction: places > 0 ? digits.slice(-places) : '' }
}

function grouped(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function plain(value: Decimal, group: boolean): string {
  const whole = group ? grouped(value.whole) : value.whole
  return `${value.negative ? '-' : ''}${whole}${value.fraction ? `.${value.fraction}` : ''}`
}

/**
 * A count or rate: grouped, at most one decimal, and no `.0` on a whole number
 * (`format_number`). A whole number is written from `int()`, which has no
 * negative zero: -0.04 reads "0", where a percentage keeps "-0.0%".
 */
function formatNumber(value: Decimal): string {
  const rounded = quantize(value, 1)
  if (rounded.fraction !== '0') return plain(rounded, true)
  return plain({ negative: rounded.negative && rounded.whole !== '0', whole: rounded.whole, fraction: '' }, true)
}

/** The symbols `Intl.NumberFormat('en-GB')` uses, as `formatting.py` lists them; any other currency is its code and a space. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: 'US$',
  JPY: 'JP¥',
  AUD: 'A$',
  CAD: 'CA$',
  NZD: 'NZ$',
  HKD: 'HK$',
}

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? `${currency} `
}

/** Whole minor units as money with every minor digit: `-123456 GBP` → `-£1,234.56` (`format_money`). */
function formatMoney(minor: Decimal, currency: string): string {
  const exponent = minorUnitExponent(currency)
  const major = quantize(shift(minor, -exponent), exponent)
  return `${major.negative ? '-' : ''}${currencySymbol(currency)}${plain({ ...major, negative: false }, true)}`
}

/** Kinds whose number counts tickets: "120 tickets a day", not "120 a day". */
const TICKET_KINDS: ReadonlySet<string> = new Set([
  'competition_velocity',
  'competition_tickets_per_hour',
  'competition_sales_slowdown',
  'competition_projected_miss',
])
/** Money kinds that are a rate, not a total. */
const MONEY_PER_HOUR_KINDS: ReadonlySet<string> = new Set(['competition_order_value_per_hour'])
/** Ratios that are a return on spend: "£3.10 per £1" in the reporting currency. */
const RETURN_ON_SPEND_KINDS: ReadonlySet<string> = new Set(['roas', 'capture_corrected_roas'])

/** The API's own names (`insight_writer/formatting.py`), so a date reads the same in an alert, a trend and a digest. */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** An ISO date (`2026-10-09`) as "Fri 9 Oct 2026", read as a calendar day (no time zone shift). */
export function formatFactDate(iso: string): string {
  return formatCalendarDay(iso, true)
}

/** A fact's period: one day, or its first and last day. */
export function formatFactPeriod(start: string, end: string): string {
  return start === end ? formatFactDate(start) : `${formatFactDate(start)} to ${formatFactDate(end)}`
}

/** "Mon 12 Oct", as `format_date` writes a fact's date value. */
function formatShortDate(iso: string): string {
  return formatCalendarDay(iso, false)
}

/**
 * The one reading of a calendar day: an ISO date's first ten characters as a
 * UTC day (no time zone shift), named from the fixed tables; anything that is
 * not a date is returned as it came.
 */
function formatCalendarDay(iso: string, withYear: boolean): string {
  const day = new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(day.getTime())) return iso
  const named = `${WEEKDAYS[day.getUTCDay()]} ${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]}`
  return withYear ? `${named} ${day.getUTCFullYear()}` : named
}

const UTC_FALLBACK = 'UTC'

/**
 * An instant in the tenant's reporting time zone, as `format_datetime` writes
 * it: "Mon 12 Oct, 18:00". Until the zone is known (or if this browser does not
 * know it) it is written in UTC and says so — never silently in the viewer's
 * own zone.
 */
function formatInstant(iso: string, timeZone: string | null): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  let parts: Intl.DateTimeFormatPart[]
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone ?? UTC_FALLBACK,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(at)
  } catch {
    return formatInstant(iso, null)
  }
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((candidate) => candidate.type === type)?.value)
  // The local calendar day, named from the fixed tables rather than this browser's locale data.
  const day = new Date(Date.UTC(part('year'), part('month') - 1, part('day')))
  const time = `${String(part('hour')).padStart(2, '0')}:${String(part('minute')).padStart(2, '0')}`
  return `${formatShortDate(day.toISOString())}, ${time}${timeZone ? '' : ' UTC'}`
}

/** How the tenant reports: the currency a return on spend is written in, and the zone an instant is written in. */
export interface ReportingContext {
  currency: string | null
  timeZone: string | null
}

/** What a stored fact's value is about: its kind, unit and currency (the facts contract). */
export interface FactShape {
  kind: string
  unit: FactUnit
  currency: string | null
}

/**
 * A stored fact's value in its unit, as `FactFormatter` writes it. A value
 * that is not a finite decimal, or money without a currency, is shown as it
 * came rather than as a figure it is not. A return on spend is written in the
 * tenant's reporting currency (a plain ratio until it is known), an instant in
 * its reporting time zone (UTC, saying so, until it is known).
 */
export function formatFactValue(value: string, fact: FactShape, reporting: ReportingContext): string {
  if (fact.unit === 'date') return formatShortDate(value)
  if (fact.unit === 'datetime') return formatInstant(value, reporting.timeZone)
  const number = parseDecimal(value)
  if (!number) return value
  switch (fact.unit) {
    case 'money_minor':
      if (!fact.currency) return value
      return `${formatMoney(number, fact.currency)}${MONEY_PER_HOUR_KINDS.has(fact.kind) ? ' an hour' : ''}`
    case 'fraction':
      return `${plain(quantize(shift(number, 2), 1), false)}%`
    case 'ratio': {
      const ratio = plain(quantize(number, 2), false)
      if (!RETURN_ON_SPEND_KINDS.has(fact.kind) || !reporting.currency) return `${ratio}×`
      const symbol = currencySymbol(reporting.currency)
      return `${symbol}${ratio} per ${symbol}1`
    }
    case 'hours': {
      const figure = formatNumber(number)
      return `${figure} ${figure === '1' ? 'hour' : 'hours'}`
    }
    case 'count':
    case 'per_day':
    case 'per_hour': {
      const figure = formatNumber(number)
      const noun = TICKET_KINDS.has(fact.kind) ? (figure === '1' ? ' ticket' : ' tickets') : ''
      const rate = fact.unit === 'per_day' ? ' a day' : fact.unit === 'per_hour' ? ' an hour' : ''
      return `${figure}${noun}${rate}`
    }
  }
}
